import { Navigate } from 'react-router-dom';
import useFeaturePermissions from '../../hooks/useFeaturePermissions';
import useParentRegistrationStatus from '../../hooks/useParentRegistrationStatus';
import useModuleAccess from '../../hooks/useModuleAccess';

// Fun animated loading component for kids
const AnimatedLoadingScreen = ({ message }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-yellow-100 via-pink-100 to-purple-100 overflow-hidden">
    {/* Animated floating shapes background */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating stars */}
      <div className="absolute top-[10%] left-[10%] text-4xl animate-bounce" style={{ animationDuration: '2s', animationDelay: '0s' }}>⭐</div>
      <div className="absolute top-[20%] right-[15%] text-3xl animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.3s' }}>🌟</div>
      <div className="absolute bottom-[30%] left-[20%] text-3xl animate-bounce" style={{ animationDuration: '2.2s', animationDelay: '0.6s' }}>✨</div>
      
      {/* Floating hearts */}
      <div className="absolute top-[40%] right-[10%] text-3xl animate-pulse" style={{ animationDuration: '1.5s' }}>💖</div>
      <div className="absolute bottom-[20%] right-[25%] text-2xl animate-pulse" style={{ animationDuration: '1.8s', animationDelay: '0.4s' }}>💕</div>
      
      {/* Floating clouds */}
      <div className="absolute top-[5%] left-[30%] text-5xl opacity-60 animate-float-slow">☁️</div>
      <div className="absolute top-[15%] right-[30%] text-4xl opacity-50 animate-float-slow" style={{ animationDelay: '1s' }}>☁️</div>
      
      {/* Rainbow arc */}
      <div className="absolute bottom-[10%] left-[5%] text-6xl animate-pulse" style={{ animationDuration: '3s' }}>🌈</div>
      
      {/* Animated gradient blobs */}
      <div className="absolute -top-20 -left-20 w-72 h-72 bg-pink-300/40 rounded-full blur-3xl animate-blob"></div>
      <div className="absolute -bottom-20 -right-20 w-72 h-72 bg-purple-300/40 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-yellow-200/30 rounded-full blur-3xl animate-blob" style={{ animationDelay: '4s' }}></div>
    </div>
    
    <div className="relative z-10 flex flex-col items-center gap-6">
      {/* Logo with bounce animation */}
      <div className="relative">
        {/* Glow effect behind logo */}
        <div 
          className="absolute inset-0 w-36 h-36 rounded-full blur-2xl -z-10"
          style={{ 
            background: 'linear-gradient(135deg, #FFD54F 0%, #FF69B4 50%, #4B3F8C 100%)', 
            opacity: 0.5,
            animation: 'pulse 2s ease-in-out infinite'
          }}
        />
        
        {/* Logo image with bounce */}
        <img 
          src="/logo-destiny.png" 
          alt="Taska Ummu Qaseh" 
          className="w-32 h-32 object-contain drop-shadow-2xl animate-bounce-slow"
          onError={(e) => {
            // Fallback if logo not found - show text
            e.target.style.display = 'none';
          }}
        />
        
        {/* Sparkles around logo */}
        <div className="absolute -top-2 -right-2 text-2xl animate-spin-slow">✨</div>
        <div className="absolute -bottom-1 -left-2 text-xl animate-ping" style={{ animationDuration: '1.5s' }}>💫</div>
        <div className="absolute top-0 -left-4 text-lg animate-pulse">⭐</div>
      </div>
      
      {/* Loading text with wave animation */}
      <div className="text-center">
        <h2 className="text-3xl font-black bg-gradient-to-r from-orange-500 via-pink-500 to-purple-500 bg-clip-text text-transparent mb-3">
          TASKA UMMU QASEH
        </h2>
        
        {/* Bouncing dots loader */}
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg font-bold text-pink-500">Sedang memuatkan</span>
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
          </div>
        </div>
      </div>
      
      {/* Fun message */}
      <p className="text-sm text-gray-500 font-medium animate-pulse">
        Sediakan senyuman anda! 😊
      </p>
    </div>
    
    {/* Custom CSS animations */}
    <style>{`
      @keyframes float-slow {
        0%, 100% { transform: translateY(0) translateX(0); }
        25% { transform: translateY(-10px) translateX(5px); }
        50% { transform: translateY(-5px) translateX(-5px); }
        75% { transform: translateY(-15px) translateX(3px); }
      }
      @keyframes bounce-slow {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-15px); }
      }
      @keyframes spin-slow {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes blob {
        0%, 100% { transform: scale(1) translate(0, 0); }
        33% { transform: scale(1.1) translate(20px, -20px); }
        66% { transform: scale(0.9) translate(-10px, 10px); }
      }
      .animate-float-slow {
        animation: float-slow 6s ease-in-out infinite;
      }
      .animate-bounce-slow {
        animation: bounce-slow 1.5s ease-in-out infinite;
      }
      .animate-spin-slow {
        animation: spin-slow 3s linear infinite;
      }
      .animate-blob {
        animation: blob 7s ease-in-out infinite;
      }
    `}</style>
  </div>
);

// Map route paths to feature IDs
const ROUTE_FEATURE_MAP = {
  '/activities': 'activities',
  '/nutrition': 'nutrition',
  '/daily-logs': 'daily_logs',
  '/development': 'development',
  '/nutritional-status': 'nutritional_status',
  '/children': 'children',
  '/classes': 'children',
  '/reports': 'reports',
  '/billing': 'invoices',
};

// Routes that require child registration for parents
const PARENT_RESTRICTED_ROUTES = [
  '/activities',
  '/daily-logs',
  '/children',
  '/takwim',
  '/classes',
  '/nutrition',
  '/development',
  '/nutritional-status'
];

export default function ProtectedRoute({ children, user, path }) {
  const { hasAccess, loading } = useFeaturePermissions(user?.role);
  const { status: registrationStatus, loading: regLoading } = useParentRegistrationStatus(user);
  const { isPathAllowed, loading: moduleLoading } = useModuleAccess(user?.role);
  
  // Kawalan Module Global: sekat semua role kecuali superadmin jika module OFF
  if (user?.role !== 'superadmin') {
    if (moduleLoading) {
      return <AnimatedLoadingScreen message="Memeriksa akses..." />;
    }
    if (path && !isPathAllowed(path)) {
      return <Navigate to="/" replace />;
    }
  }
  
  // Admin, Superadmin, and Branch Manager bypass all restrictions
  if (user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'branch_manager') {
    return children;
  }

  // Guru also bypasses parent registration check
  if (user?.role === 'guru') {
    // Get the feature ID for this route
    const featureId = ROUTE_FEATURE_MAP[path];
    
    // If no feature mapping, allow access
    if (!featureId) {
      return children;
    }
    
    // Show loading while checking permissions
    if (loading) {
      return <AnimatedLoadingScreen message="Memeriksa akses..." />;
    }
    
    // Check if user has access to this feature
    if (!hasAccess(featureId)) {
      return <Navigate to="/" replace />;
    }
    
    return children;
  }
  
  // For parents (ibu_bapa), check if they have registered children for restricted routes
  if (user?.role === 'ibu_bapa' && PARENT_RESTRICTED_ROUTES.includes(path)) {
    // Show loading while checking registration status
    if (regLoading) {
      return <AnimatedLoadingScreen message="Memeriksa status pendaftaran..." />;
    }
    
    // Block access if no registered children
    if (registrationStatus && !registrationStatus.can_access_features) {
      return <Navigate to="/" replace />;
    }
  }
  
  // Get the feature ID for this route
  const featureId = ROUTE_FEATURE_MAP[path];
  
  // If no feature mapping, allow access
  if (!featureId) {
    return children;
  }
  
  // Show loading while checking permissions
  if (loading) {
    return <AnimatedLoadingScreen message="Memeriksa akses..." />;
  }
  
  // Check if user has access to this feature - redirect to dashboard if not
  if (!hasAccess(featureId)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}
